import store from '@/store/index'
import useJwt from '@/cubus/jwt/useJwt'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import JSZip from 'jszip'

/** PROP-020: единственное место, где живёт этот предел. */
const LOAD_ALL_PAGE_SIZE = 100000

export default class CubusService {
  /**
   * Номер сборки фронта. Показывается в подвале и на страницах входа/регистрации —
   * по нему проверяют, что на сервере лежит именно то, что выкатили.
   *
   * ⚠️ Двигать при КАЖДОЙ выкладке, вместе с ней. 06.09 фронт уехал на боевой kbtu с
   * номером `163.260809` — сборка от 9 августа по названию и от 6 сентября по содержанию.
   * Это та же ловушка, из-за которой у бэкенда в правиле закрытия тикета стоит сверять
   * `/api/release` ДО прод-тестов: номеру верят, поэтому неверный номер хуже отсутствующего.
   *
   * Формат общий с бэкендом: 1.1.1.<номер сборки>.<ГГММДД>.<порядковый за день>.
   */
  release() {
    return '1.1.1.277.261018.01'
  }

  /**
   * PROP-020: «загрузить всё» одним числом в одном месте.
   *
   * 57 колл-сайтов кастомных форм просили справочники магическим числом в поле `pageSize`.
   * Число ничего не объясняло и жило россыпью, поэтому когда серверный потолок выборки
   * опустился ниже него, молча сломались ВСЕ 57 мест разом (BUG-026) — а чинить пришлось
   * бы каждое. Теперь предел один и правится в одной строке.
   *
   * Это по-прежнему ПОТОЛОК, а не «без ограничения»: справочник, доросший до него, снова
   * начнёт молча обрезаться. Для таких мест нужна осознанная пагинация или серверный
   * поиск — см. хвост PROP-020.
   */
  constructor() {
    this.LOAD_ALL = LOAD_ALL_PAGE_SIZE
  }

  /** PROP-020: читаемая форма запроса справочника целиком — вместо магического числа в вызове. */
  async loadAll(vm, params) {
    return this.loadQuery(vm, { ...params, pageSize: LOAD_ALL_PAGE_SIZE })
  }

  getSession() {
    const isSession = store.getters['cubus-store/IS_SESSION']
    console.log()
    if (!isSession) {
      const token = useJwt.getCubusToken()
      if (token) {
        useJwt.whoami({ token })
          .then(response => {
            console.log('whoami response', response)
            if (response.data.session) {
              store.commit('cubus-store/SET_SESSION', response.data.session)
              // this.$i18n.locale = response.data.session.user.languagesid
            } else if (response.data.error) {
              console.log('whoami response error', response.data.error)
              // this.logout(token)

              // Redirect to login page
              // this.$router.push({ name: 'auth-login' })
            }
            return store.getters['cubus-store/SESSION']
          })
          .catch(error => {
            console.log('whoami error', error)
          })
      }
    }
    return store.getters['cubus-store/SESSION']
  }

  // eslint-disable-next-line class-methods-use-this
  logout() {
    console.log('cubus.logout start')
    const token = useJwt.getCubusToken()
    if (token) useJwt.logout({ token })
    store.commit('cubus-store/CLEAR_SESSION')
    localStorage.removeItem(useJwt.jwtConfig.storageCubusTokenKeyName)
  }

  /**
   * BUG-680: причина отказа — из ответа сервера (`error`), а не «Request failed with status code 403».
   * Отказ по лимиту объёма, правам, тарифу сервер объясняет текстом — человек должен его увидеть.
   */
  errorText(msg) {
    const data = msg && msg.response && msg.response.data
    if (data && typeof data === 'object' && data.error) return this.accessErrorText(String(data.error))
    if (typeof data === 'string' && data.trim() && data.length < 500 && !data.trim().startsWith('<')) return this.accessErrorText(data.trim())
    return msg == null ? 'Неизвестная ошибка' : this.accessErrorText(msg.toString())
  }

  /**
   * BUG-781: отказ по правам бэк пишет техническим английским («Access denied: no 'update'
   * permission on object 'bdg_req_services'»), и подстроку «Access denied» читают его же ветки
   * 403 — текст меняем здесь, на показе. `names` — sid → имя объекта, когда оно известно
   * странице. Прочие сообщения возвращаются как есть.
   */
  // eslint-disable-next-line class-methods-use-this
  accessErrorText(msg, names = {}) {
    const s = String(msg == null ? '' : msg)
    const perm = {
      select: 'чтение',
      show: 'просмотр',
      insert: 'добавление записей',
      update: 'изменение записей',
      delete: 'удаление записей',
      remove: 'окончательное удаление записей',
    }
    const hint = 'Если доступ нужен, обратитесь к администратору домена.'
    let m = s.match(/Access denied: no '(\w+)' permission on (?:report )?(?:object|import) '([^']*)'/i)
    if (m) return `Нет права на ${perm[m[1]] || m[1]} в объекте «${names[m[2]] || m[2]}». ${hint}`
    m = s.match(/access denied to object '([^']*)' \((?:id|sid) = [^,)]*(?:, '(.*)')?\) for user '[^']*': no (\w+) grant/i)
    if (m) return `Нет доступа к объекту «${m[2] || names[m[1]] || m[1]}»: нет права на ${perm[m[3]] || m[3]}. ${hint}`
    m = s.match(/^object \(((?:id|sid) = [^)]*)\) not found in domain/i)
    if (m) return `Объект (${m[1]}) не найден: его нет в этом домене или он удалён.`
    return s
  }

  toastError(vm, msg) {
    vm.$toast({
      component: ToastificationContent,
      position: 'top-right',
      props: {
        title: 'Не удалось выполнить',
        icon: 'AlertTriangleIcon',
        variant: 'warning',
        text: this.errorText(msg),
      },
    })
    if (msg === 'Session not found.') {
      this.logout()
      this.pushLogin(vm)
    }
  }

  toastMessage(vm, msg) {
    vm.$toast({
      component: ToastificationContent,
      position: 'top-right',
      props: {
        title: 'Message',
        icon: 'CoffeeIcon',
        variant: 'info',
        text: msg,
      },
    })
    if (msg === 'Session not found.') {
      this.logout()
      this.pushLogin(vm)
    }
  }

  processLogin(vm, response) {
    useJwt.setCubusToken(response.data.session.token)
    vm.$store.commit('cubus-store/SET_SESSION', response.data.session)

    // const { userData } = response.data
    // useJwt.setToken(response.data.accessToken)
    // useJwt.setRefreshToken(response.data.refreshToken)
    // localStorage.setItem('userData', JSON.stringify(userData))
    // vm.$ability.update([{ action: 'manage', subject: 'all' }])

    // ? This is just for demo purpose as well.
    // ? Because we are showing eCommerce app's cart items count in navbar
    // vm.$store.commit('app-ecommerce/UPDATE_CART_ITEMS_COUNT', { eCommerceCartItemsCount: 5 })

    // ? This is just for demo purpose. Don't think CASL is role based in this case, we used role in if condition just for ease
    console.log('path before login', vm.$route.path)
    vm.$router.replace({ path: '/', query: { reload: Date.now() } })
      .then(() => {
        vm.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Welcome!',
            icon: 'CoffeeIcon',
            variant: 'success',
            text: `You have successfully logged in as ${response.data.session.user.role.name}. Now you can start to explore!`,
          },
        })
      })
  }

  isSession() {
    return store.getters['cubus-store/IS_SESSION']
  }

  setSession(sess) {
    store.commit('cubus-store/SET_SESSION', sess)
    this.$i18n.locale = sess.user.languagesid
  }

  whoami() {
    const status = store.getters['cubus-store/SESSION_STATUS']
    console.log('status', status)
    if (status === 'init') {
      const token = useJwt.getCubusToken()
      if (token) {
        store.commit('cubus-store/SET_SESSION_STATUS', 'loading')
        this.loadWhoami()
      }
    }
    return {
      status: store.getters['cubus-store/SESSION_STATUS'],
      session: store.getters['cubus-store/SESSION'],
      error: store.getters['cubus-store/SESSION_ERROR'],
    }
  }

  loadWhoami() {
    console.log('start loadWhoami')
    const token = useJwt.getCubusToken()
    useJwt.whoami({ token })
      .then(response => {
        console.log('whoami response', response)
        if (response.data.session) {
          store.commit('cubus-store/SET_SESSION', response.data.session)
        } else if (response.data.error) {
          store.commit('cubus-store/SET_SESSION_ERROR', response.data.error)
          if (response.data.error === 'Session not found.') {
            this.logout()
          }
        } else {
          store.commit('cubus-store/SET_SESSION_ERROR', 'no_session')
        }
      })
      .catch(error => {
        console.log('whoami error')
        store.commit('cubus-store/SET_SESSION_ERROR', error)
      })
  }

  loadWhoamiVm(vm) {
    const token = useJwt.getCubusToken()
    if (token) {
      useJwt.whoami({ token })
        .then(response => {
          console.log('whoami response', response)
          if (response.data.error) {
            this.toastError(vm, response.data.error)
          } else if (response.data.session) {
            vm.session = response.data.session
          }
        })
        .catch(error => {
          console.log('whoami error', error)
          this.toastError(vm, error)
        })
    } else {
      this.pushLogin(vm)
    }
  }

  isAdmin() {
    const sess = store.getters['cubus-store/SESSION']
    // console.log('sess', sess)
    return sess && sess.user
      && (
        (sess.user.sid && (sess.user.sid === 'root' || sess.user.sid === 'batman'))
        || (sess.user.roles && sess.user.roles.some(r => r.sid && r.sid === 'admin'))
      )
  }

  isRoot() {
    const sess = store.getters['cubus-store/SESSION']
    // console.log('sess', sess)
    return sess && sess.user
      && sess.user.sid && sess.user.sid === 'root'
  }

  isTester() {
    const sess = store.getters['cubus-store/SESSION']
    // console.log('sess', sess)
    return sess && sess.user
      && sess.user.sid && sess.user.sid === 'cubus_tester'
  }

  isDeveloper() {
    const sess = store.getters['cubus-store/SESSION']
    // console.log('sess for isDev', sess)
    return sess && sess.user
      && (
        (sess.user.sid && (sess.user.sid === 'root' || sess.user.sid === 'batman'))
        || (sess.user.roles && sess.user.roles.some(r => r.sid && r.sid === 'dev'))
      )
  }

  userLocale() {
    const sess = store.getters['cubus-store/SESSION']
    if (sess) {
      return sess.user.languagesid
    }
    return 'en'
  }

  loadObjectBySidVm(vm, param) {
    /* const defaultParam = {
      objectsid: 'mount',
      domainsid: '',
    } */
    useJwt.query({
      query: {
        method: 'objectbysid',
        param,
      },
    })
      .then(response => {
        console.log('object response', response)
        if (response.data.thread) {
          this.delayObjectBySid(response.data.thread, vm)
        } else if (response.data.error) {
          this.toastError(vm, response.data.error)
        }
      })
      .catch(error => {
        console.log('object error', error)
      })
  }

  delayObjectBySid(threadName, vm) {
    setTimeout(() => this.threadObjectBySid(threadName, vm), 500)
  }

  threadObjectBySid(threadName, vm) {
    useJwt.query({
      query: {
        method: 'thread',
        param: {
          threadname: threadName,
        },
      },
    })
      .then(response => {
        console.log('thread response', response)
        if (response.data.thread) {
          if (response.data.thread.error) {
            this.toastError(vm, response.data.thread.error)
          } else if (response.data.thread.status === 'done') {
            console.log('threadObjectBySid', response)
            vm.object = response.data.thread.result
          } else {
            this.delayObjectBySid(threadName, vm)
          }
        }
        if (response.data && response.data.error) {
          this.toastError(vm, response.data.error)
        }
      })
      .catch(error => {
        console.log('thread error', error)
      })
  }

  pushLogin(vm) {
    vm.$router.push({ name: 'auth-login' })
  }

  filterBy(option, label, search) {
    const pattern = `.*${search
      .replace(new RegExp(' ', 'gi'), '.*')
      .replace(new RegExp('\\.\\*\\.\\*', 'gi'), ' ')}.*`
    const re = new RegExp(pattern, 'gi')
    return re.test(label || '')
  }

  addThousandSeparator(integer, separator) {
    return integer.toString().replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`)
  }

  defaultFormatOptions = {
    prefix: '',
    suffix: '',
    decimal: ',',
    thousand: '.',
    precision: 2,
    acceptNegative: true,
    isInteger: false,
  }

  formatNumber(input, opt) {
    if (input === null) input = 0
    const mergedOptions = { ...this.defaultFormatOptions, ...opt }
    if (typeof input === 'number' && !mergedOptions.isInteger) {
      input = input.toFixed(this.fixed(mergedOptions.precision))
    }
    const negative = this.isNegative(input, mergedOptions.acceptNegative) ? '-' : ''
    const numbers = this.onlyNumbers(input)
    const currency = this.numbersToCurrency(numbers, mergedOptions.precision)
    const parts = this.toStr(currency).split('.')
    let integer = parts[0]
    const decimal = parts[1]
    integer = this.addThousandSeparator(integer, mergedOptions.thousand)
    return negative + mergedOptions.prefix + this.joinIntegerAndDecimal(integer, decimal, mergedOptions.decimal) + mergedOptions.suffix
  }

  formatPercent(val) {
    return `${(parseFloat(val) * 100).toFixed(2)}%`
  }

  isNegative(string, acceptNegative = true) {
    if (!acceptNegative) return false
    if (typeof string !== 'string') string = string.toString()
    const forcePositive = string.indexOf('+') >= 0
    const isNegative = !!((string !== 0 && string.indexOf('-') >= 0 || string[string.length - 1] == '-'))
    return !!((!forcePositive && isNegative))
  }

  onlyNumbers(input) {
    return this.toStr(input).replace(/\D+/g, '') || '0'
  }

  toStr(value) {
    return value ? value.toString() : ''
  }

  numbersToCurrency(numbers, precision) {
    const exp = Math.pow(10, precision)
    const float = parseFloat(numbers) / exp
    return float.toFixed(this.fixed(precision))
  }

  fixed(precision) {
    return Math.max(0, Math.min(precision, 20))
  }

  joinIntegerAndDecimal(integer, decimal, separator) {
    return decimal ? integer + separator + decimal : integer
  }

  unformatNumber(input, opt = { precision: 2, isInteger: false, acceptNegative: true }) {
    if (input === null) input = 0
    const mergedOptions = { ...defaultOptions, ...opt }
    const negative = (isNegative(input, mergedOptions.acceptNegative)) ? -1 : 1
    const numbers = onlyNumbers(input)
    const currency = numbersToCurrency(numbers, mergedOptions.precision)
    if (mergedOptions.isInteger) {
      return parseInt(`${isNegative(input, mergedOptions.acceptNegative) ? '-' : ''}${numbers.toString()}`)
    }
    return parseFloat(currency) * negative
  }

  randomString() {
    return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1)
  }

  guid() {
    // return this.randomString() + this.randomString() + '-' + this.randomString() + '-' + this.randomString() + '-' + this.randomString() + '-' + this.randomString() + this.randomString() + this.randomString()
    return `${this.randomString()}${this.randomString()}-${this.randomString()}-${this.randomString()}-${this.randomString()}-${this.randomString()}${this.randomString()}${this.randomString()}`
  }

  setPageTitle(title) {
    // document.title = title ? `Cubus﹒${title}` : 'Cubus﹒Business Automation Platform'
    document.title = title || 'Cubus﹒Business Automation Platform'
  }

  // Единая схема статусов BPM (этап и экземпляр) — цвет бейджа + русский лейбл.
  bpmStatusVariant(code) {
    return {
      DRAFT: 'secondary',
      ACTIVE: 'primary',
      ASSIGNED: 'warning',
      IN_PROGRESS: 'info',
      WAITING: 'info',
      SUSPENDED: 'secondary',
      COMPLETED: 'success',
      ABORTED: 'danger',
      CANCELLED: 'danger',
    }[code] || 'secondary'
  }

  bpmStatusLabel(code) {
    return {
      DRAFT: 'Черновик',
      ACTIVE: 'Активен',
      ASSIGNED: 'Назначена',
      IN_PROGRESS: 'В работе',
      WAITING: 'Ждёт подпроцесс',
      SUSPENDED: 'Приостановлен',
      COMPLETED: 'Завершён',
      ABORTED: 'Прерван',
      CANCELLED: 'Отменён',
    }[code] || code || ''
  }

  attributeToField(attr) {
    return {
      title: attr.label,
      value: attr.entityid,
      datatype: attr.datatype,
      attributeid: attr.attributeid,
      entitysid: attr.entitysid,
      parententityid: attr.parententityid,
      refobjectid: attr.refobjectid,
    }
  }

  locale(loc) {
    if (loc === 'ru') return 'ru-RU'
    return 'en-US'
  }

  isCredentialAvailable(access, permissiontype) {
    return access && access[permissiontype]
      && (access[permissiontype] === 'grant' || access[permissiontype] === 'withgrant')
  }

  async zipFiles(files) {
    const zip = new JSZip()
    // Добавляем каждый выбранный файл в архив
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      // eslint-disable-next-line no-await-in-loop
      const fileContent = await this.readFileContent(file)
      zip.file(file.name, fileContent, { compression: 'DEFLATE' }) // Указываем сжатие для файла
    }
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    return zipBlob
  }

  async readFileContent(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
      reader.readAsArrayBuffer(file)
    })
  }

  #loadObjectByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'objectfull',
        mode: 'sync',
        param: {
          objectid: params.objectid,
          objectsid: params.objectsid,
          domainsid: params.domainsid,
          entityid: params.entityid,
        },
      },
    }
    return useJwt.query(args)
  }

  async loadObject(vm, params) {
    try {
      const response = await this.#loadObjectByParams(params)
      if (response.data.result) return response.data.result.objectfull
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  /** Как loadObject, но без тоста: { objectfull } или { error } — страница сама скажет, что не так. */
  async loadObjectOrError(params) {
    try {
      const response = await this.#loadObjectByParams(params)
      if (response.data.result && response.data.result.objectfull) return { objectfull: response.data.result.objectfull }
      return { error: this.accessErrorText(response.data.error || 'Объект не найден.') }
    } catch (e) {
      console.error(e)
      return { error: this.errorText(e) }
    }
  }

  #loadQueryByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: params.method || 'query',
        mode: 'sync',
        param: {
          keymode: params.keymode || 'field',
          objectid: params.objectid || null,
          objectsid: params.objectsid || null,
          domainsid: params.domainsid || null,
          filter: params.filter || null,
          groupby: params.groupby || null,
          orderby: params.orderby || null,
          limit: params.pageSize || params.limit || 100,
          offset: params.offset || 0,
          withsubrecords: params.withsubrecords || false,
          paramrecord: params.paramRecord || params.paramrecord || null,
          entityids: params.entityids || [],
        },
      },
    }
    return useJwt.query(args)
  }

  async loadQuery(vm, params) {
    try {
      const response = await this.#loadQueryByParams(params)
      if (response.data.result) return response.data.result.query
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  #saveRecordByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'saverecord',
        mode: 'sync',
        param: {
          keymode: params.keymode,
          objectsid: params.objectsid,
          objectid: params.objectid,
          record: params.record,
          ...(params.formid ? { formid: params.formid } : {}),
        },
      },
    }
    return useJwt.query(args)
  }

  async saveRecord(vm, params) {
    try {
      const response = await this.#saveRecordByParams(params)
      if (response.data.result) return response.data.result.record
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  deleteRecordByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'deleterecord',
        mode: 'sync',
        param: {
          objectid: params.objectid,
          objectsid: params.objectsid,
          record: params.record,
          // CUB-1310: удаление через форму — сервер проверит право на форму
          ...(params.formid ? { formid: params.formid } : {}),
        },
      },
    }
    return useJwt.query(args)
  }

  async delRecord(vm, params) {
    try {
      const response = await this.deleteRecordByParams(params)
      if (response.data.result) return response.data.result.record
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  removeRecordByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'removerecord',
        mode: 'sync',
        param: {
          objectid: params.objectid,
          keymode: params.keymode || 'sid',
          record: params.record,
        },
      },
    }
    return useJwt.query(args)
  }

  async removeRecord(vm, params) {
    try {
      const response = await this.removeRecordByParams(params)
      if (response.data.result) return response.data.result.record
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  removeRecordUnsafeByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'removerecordunsafe',
        mode: 'sync',
        param: {
          objectid: params.objectid,
          keymode: params.keymode || 'sid',
          record: params.record,
        },
      },
    }
    return useJwt.query(args)
  }

  async removeRecordUnsafe(vm, params) {
    try {
      const response = await this.removeRecordUnsafeByParams(params)
      if (response.data.result) return response.data.result.record
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadArgumentTypesByParams() {
    const args = {
      query: {
        method: 'argumenttypes',
        mode: 'sync',
        param: {},
      },
    }
    return useJwt.query(args)
  }

  async loadArgumentTypes(vm) {
    try {
      const response = await this.loadArgumentTypesByParams()
      // console.log('loadArgumentTypes.response', response)
      if (response.data.result) return response.data.result.types
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadRefByParams(params) {
    const args = {
      query: {
        method: 'entref',
        mode: 'sync',
        param: {
          entityid: params.entityid || 0,
          entitysid: params.entitysid || '',
        },
      },
    }
    return useJwt.query(args)
  }

  async loadRef(vm, params) {
    try {
      const response = await this.loadRefByParams(params)
      if (response.data.result) return response.data.result.ref
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  dateToString(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // Месяц с ведущим нулём (январь - 0)
    const day = String(date.getDate()).padStart(2, '0') // Дата с ведущим нулём
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const formatted = `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`
    return formatted
  }

  dateToStringUTC(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // Месяц с ведущим нулём (январь - 0)
    const day = String(date.getDate()).padStart(2, '0') // Дата с ведущим нулём
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    return formatted
  }

  filter_notDel() {
    return {
      isactive: true,
      node: {
        isactive: true,
        oper: { sid: 'and' },
        nodes: [],
        conds: [
          {
            isactive: true,
            oper: { sid: 'equal' },
            args: [
              { type: { sid: 'entitysid' }, value: 'del' },
              { type: { sid: 'value' }, value: false },
            ],
          },
        ],
      },
    }
  }

  cond_notDel() {
    return {
      isactive: true,
      oper: { sid: 'equal' },
      args: [
        { type: { sid: 'entitysid' }, value: 'del' },
        { type: { sid: 'value' }, value: false },
      ],
    }
  }

  orderby_code() {
    return {
      isActive: true,
      sorts: [
        {
          isActive: true,
          oper: { sid: 'asc' },
          entity: { sid: 'code' },
        },
      ],
    }
  }

  orderbyName() {
    return {
      isActive: true,
      sorts: [
        {
          isActive: true,
          oper: { sid: 'asc' },
          entity: { sid: 'name' },
        },
      ],
    }
  }

  orderby_date() {
    return {
      isActive: true,
      sorts: [
        {
          isActive: true,
          oper: { sid: 'asc' },
          entity: { sid: 'date' },
        },
      ],
    }
  }

  orderby_dateDesc() {
    return {
      isActive: true,
      sorts: [
        {
          isActive: true,
          oper: { sid: 'desc' },
          entity: { sid: 'date' },
        },
      ],
    }
  }

  monthNames() {
    return ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
  }

  runMountByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'runmount',
        mode: 'sync',
        param: {
          keymode: params.keymode || 'field',
          objectid: params.objectid,
          objectsid: params.objectsid,
          record: params.record,
        },
      },
    }
    return useJwt.query(args)
  }

  async runMount(vm, params) {
    try {
      const response = await this.runMountByParams(params)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  runProcedureByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'runprocedure',
        mode: 'sync',
        param: {
          keymode: params.keymode || 'field',
          objectid: params.objectid,
          objectsid: params.objectsid,
          record: params.record,
        },
      },
    }
    return useJwt.query(args)
  }

  async runProcedure(vm, params) {
    try {
      const response = await this.runProcedureByParams(params)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadNewRecordByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'newrecord',
        mode: 'sync',
        param: {
          keymode: params.keymode || 'sid',
          objectid: params.objectid || null,
          objectsid: params.objectsid || null,
          filter: params.filter || null,
          userfilter: params.userFilter || null,
        },
      },
    }
    return useJwt.query(args)
  }

  async clearBackendCache() {
    try {
      const response = await useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'clearcache', mode: 'sync', param: {} },
      })
      return response.data
    } catch (e) {
      console.error('clearBackendCache error', e)
      return null
    }
  }

  async loadNewRecord(vm, params) {
    try {
      const response = await this.loadNewRecordByParams(params)
      // console.log('loadNewRecord response', response)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadEntityByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'entity',
        mode: 'sync',
        param: {
          entityid: params.entityid || null,
          entitysid: params.entitysid || null,
        },
      },
    }
    return useJwt.query(args)
  }

  async loadEntity(vm, params) {
    try {
      const response = await this.loadEntityByParams(params)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadEntitiesByParam(param) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'entities',
        mode: 'sync',
        param: {
          entityids: param.entityids || [],
        },
      },
    }
    return useJwt.query(args)
  }

  async loadEntities(vm, param) {
    try {
      const response = await this.loadEntitiesByParam(param)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadScalarValueByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'scalarvalue',
        mode: 'sync',
        param: {
          scalarid: params.scalarid || null,
          record: params.record || null,
        },
      },
    }
    return useJwt.query(args)
  }

  async loadScalarValue(vm, params) {
    try {
      const response = await this.loadScalarValueByParams(params)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  loadMultiQueryByParams(params) {
    const args = {
      token: useJwt.getCubusToken(),
      query: {
        method: 'multiquery',
        mode: 'sync',
        param: {
          requests: params.requests,
        },
      },
    }
    return useJwt.query(args)
  }

  async loadMultiQueryV1(vm, params) {
    try {
      const response = await this.loadMultiQueryByParams(params)
      if (response.data.result) return response.data.result
      if (response.data.error) this.toastError(vm, response.data.error)
      return null
    } catch (e) {
      console.error(e)
      this.toastError(vm, e)
      return null
    }
  }

  async loadMultiQuery(vm, params) {
    const requests = params.requests || []
    const stepSize = 20
    // Разбиваем на чанки по stepSize. Раньше чанки выполнялись последовательным
    // await в цикле — запрос на ~220 подзапросов (11 чанков) шёл друг за другом и
    // открытие формы занимало ~5с. Теперь чанки идут параллельно, но с ограничением
    // одновременных запросов (CONCURRENCY), чтобы большие формы не выстреливали
    // сотней соединений и не нагружали бэкенд. Порядок результатов сохраняем по
    // индексу чанка.
    const chunks = []
    for (let i = 0; i < requests.length; i += stepSize) {
      chunks.push(requests.slice(i, i + stepSize))
    }
    const CONCURRENCY = 6
    const perChunk = new Array(chunks.length)
    let next = 0
    const worker = async () => {
      while (next < chunks.length) {
        const idx = next
        next += 1
        try {
          const response = await this.loadMultiQueryByParams({ requests: chunks[idx] })
          if (response.data.result && response.data.result.result.results) {
            perChunk[idx] = response.data.result.result.results
          } else {
            if (response.data.error) this.toastError(vm, response.data.error)
            perChunk[idx] = []
          }
        } catch (e) {
          console.error(e)
          this.toastError(vm, e)
          perChunk[idx] = []
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, () => worker()))
    const results = []
    perChunk.forEach(chunkResults => results.push(...(chunkResults || [])))
    return { result: { results } }
  }

  emptyFilter() {
    const res = {
      node: {
        isactive: false,
        isexpanded: false,
        oper: {
          id: 1,
          sid: 'and',
          name: 'AND',
        },
        nodes: [],
        conds: [],
      },
    }
    return res
  }

  emptyArgument() {
    const res = {
      argtype: {
        value: 2,
        sid: 'value',
        title: 'Value',
      },
      argvalue: {
        value: null,
        sid: null,
        title: null,
      },
    }
    return res
  }
}
